import { useEffect } from 'react';
import { ButtonHeadless, Modal, Spin } from '@wajeez-web/components/ui';
import { SubmitHandler, useForm } from 'react-hook-form';
import {
  FormTextInputV2,
  FormDatePickerV2Input,
} from '@wajeez-web/components/form-builder';
import { Server } from '@wajeez/wajeez.microservices';
import { ReturnTranslatedText } from '@wajeez-web/core/localization';
import { BiLoaderAlt } from 'react-icons/bi';
import {
  PromoCodesActions,
  useProductDispatch,
  useProductSelector,
} from '@wajeez-web/stores/product';

type Input = Server.UpdatePromoCodeInput;

interface PromoCodeModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit?: () => void | Promise<void>;
  id?: number;
}

export const PromoCodeModal = ({
  isOpen,
  onClose,
  onSubmit,
  id,
}: PromoCodeModalProps) => {
  const productDispatch = useProductDispatch();

  const createPromoCodeStatus = useProductSelector(
    ({ promoCodes }) => promoCodes.createPromoCode.status
  );
  const editPromoCodeStatus = useProductSelector(
    ({ promoCodes }) => promoCodes.editPromoCode.status
  );
  const promoCodeById = useProductSelector(
    ({ promoCodes }) => promoCodes.promoCodeById
  );

  const { handleSubmit, control, reset } = useForm<Input>({
    defaultValues: {},
  });

  const onFormSubmit: SubmitHandler<Input> = async (data) => {
    let res: any;

    if (!id) {
      res = await productDispatch(
        PromoCodesActions.createPromoCodeAsync({
          code: data.name,
          ...data,
        })
      );
    } else {
      res = await productDispatch(
        PromoCodesActions.editPromoCodeAsync({
          id,
          ...data,
        })
      );
    }

    if (res.meta.requestStatus === 'fulfilled') {
      onSubmit && (await onSubmit());
    }
  };

  useEffect(() => {
    if (promoCodeById.data) {
      reset(promoCodeById.data);
    }
  }, [promoCodeById.data]);

  useEffect(() => {
    if (id) {
      productDispatch(PromoCodesActions.getPromoCodeByIdAsync(id));
    }

    return () => {
      productDispatch(PromoCodesActions.clearPromoCode());
    };
  }, [id, productDispatch]);

  const isLoading = promoCodeById.status === 'loading';

  return (
    <Modal
      open={isOpen}
      onClose={onClose}
      title="Promo Code"
      alertTarget="modal"
      fontClassName="font-[Tajawal]"
      withScroll
    >
      <div className="w-full">
        {isLoading ? (
          <div className="w-full min-h-[200px] flex justify-center items-center">
            <Spin />
          </div>
        ) : (
          <form
            onSubmit={handleSubmit(onFormSubmit)}
            noValidate
            className="w-full flex flex-col justify-start items-start"
          >
            <div className="w-[300px] flex flex-col justify-start items-start space-y-2">
              <FormTextInputV2
                name="name"
                control={control}
                fieldMeta={{
                  fieldType: Server.FieldType.Text,
                  fieldOptions: {},
                  required: true,
                  label: 'Name',
                  placeHolder: 'Name',
                }}
                required
              />
              <div className="w-full">
                <FormDatePickerV2Input
                  name="expiresOn"
                  control={control}
                  fieldMeta={{
                    fieldType: Server.FieldType.Date,
                    fieldOptions: {},
                    label: 'Expires On',
                    placeHolder: 'Expires On',
                  }}
                />
              </div>
            </div>
            <div className="mt-6 w-full flex justify-end items-center space-s-2">
              <div className="w-[70px]">
                <ButtonHeadless
                  bgStyle="bg-warmGray-300"
                  borderStyle="border-none"
                  paddingYStyle="py-1"
                  action={onClose}
                >
                  <div className="text-warmGray-600 text-[16px] font-medium">
                    <ReturnTranslatedText
                      text="buttons.Cancel"
                      origintext="Cancel"
                    />
                  </div>
                </ButtonHeadless>
              </div>
              <div className="w-[70px]">
                <ButtonHeadless
                  type="submit"
                  borderStyle="border-none"
                  paddingYStyle="py-1"
                >
                  <div className="text-white text-[16px] font-medium flex justify-center items-center space-s-2">
                    <ReturnTranslatedText
                      text="buttons.Save"
                      origintext="Save"
                    />
                    {(createPromoCodeStatus === 'loading' ||
                      editPromoCodeStatus === 'loading') && (
                      <BiLoaderAlt className="animate-spin w-5" />
                    )}
                  </div>
                </ButtonHeadless>
              </div>
            </div>
          </form>
        )}
      </div>
    </Modal>
  );
};
